import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  Box,
  Autocomplete,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import Task from "@mui/icons-material/Task";
import ReportNavigation from "../../Components/ReportNavigation/ReportNavigation";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ReportLoadingDialog from "../../Components/ReportLoadingDialog";

const StockLedgerBinCard = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const redirectToLoginPage = () => {
    if (!userToken) return <Redirect exact to="/sparepartsLogin" />;
  };

  // TIMEOUT WHEN JWT EXPIRES
  setTimeout(() => {
    window.location.reload(false);
  }, 1800000);

  const date = new Date();
  const currentYear = new Date().getFullYear();
  const initialDate = new Date(currentYear, 3, 1);

  const columns = useMemo(
    () => [
      { field: "srlno", headerName: "Srl", width: 70, sortable: false },
      { field: "itemcode", headerName: "Item Code", width: 160 },
      { field: "itemname", headerName: "Item Name", width: 360 },
      //{ field: "rackname", headerName: "Rack", width: 100 },
    ],
    []
  );

  const [itemarray, setitemarray] = useState([]);
  const [allparts, setallparts] = useState([]);

  const [fromdate, setfromdate] = useState(initialDate);
  const [todate, settodate] = useState(date);
  const [pdfStream, setPdfStream] = useState(null);

  const [loading, setLoading] = useState(false);
  const [genrating, setgenrating] = useState(false);
  const [reportText, setReportText] = useState({ title: "", subtitle: "" });

  // ✅ Manual default (no modal)
  const [mode, setMode] = useState("manual");

  // AUTO
  const [partno, setpartno] = useState(null);
  const [autoFetching, setAutoFetching] = useState(false);

  // MANUAL
  const [manualCode, setManualCode] = useState("");
  const [manualFetching, setManualFetching] = useState(false);

  // show/hide add panel (optional)
  const [showAddPanel, setShowAddPanel] = useState(false);

  // refs for focus
  const manualInputRef = useRef(null);
  const autoInputRef = useRef(null);

  async function getfromdatedata() {
    try {
      const response = await fetch("/reports/fromdate", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setfromdate(jsonData.fromdate);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getfromdatedata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getallparts() {
    try {
      const response = await fetch("/allparts", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setallparts(jsonData);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getallparts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const OPTIONS_LIMIT = 15;
  const filterOptions = createFilterOptions({
    limit: OPTIONS_LIMIT,
    trim: true,
  });

  const fetchItemDetail = useCallback(
    async (code) => {
      const apicall = await fetch("/allparts/detailfetch", {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      const json = await apicall.json();

      // your backend returns {type:"error"} in 200
      if (json?.type === "error") throw new Error(json.message || "No Parts Found!");
      if (!apicall.ok) throw new Error(json?.message || "Failed to fetch item detail");

      return json;
    },
    [userToken]
  );

  const addRowFromDetail = useCallback((detail) => {
    const exists = itemarray.some(
      (r) =>
        String(r.itemid) === String(detail.itemid) ||
        String(r.itemcode) === String(detail.itemcode)
    );
    if (exists) {
      toast.warning("Already added");
      return false;
    }

    setitemarray((prev) => [
      ...prev,
      {
        srlno: prev.length + 1,
        itemcode: detail.itemcode || "",
        itemname: detail.itemname || "",
        rackname: detail.rackname || "",
        itemid: detail.itemid || null,
      },
    ]);

    toast.success(`Added: ${detail.itemcode} - ${detail.itemname}`);
    return true;
  }, [itemarray]);

  // ✅ MANUAL (default): Enter => fetch + add (no mouse)
  const handleManualEnter = async () => {
    const code = String(manualCode || "").trim();
    if (!code) return;

    try {
      setManualFetching(true);
      const detail = await fetchItemDetail(code.toUpperCase());
      addRowFromDetail(detail);

      setManualCode("");
      setTimeout(() => manualInputRef.current?.focus?.(), 60);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not fetch");
      setTimeout(() => manualInputRef.current?.focus?.(), 60);
    } finally {
      setManualFetching(false);
    }
  };

  // ✅ AUTO: select => fetch + add immediately
  const handleAutoChange = async (event, newValue) => {
    setpartno(newValue);
    if (!newValue) return;

    const code = String(newValue.itemcode || "").trim();
    if (!code) return toast.error("Invalid item code");

    try {
      setAutoFetching(true);
      const detail = await fetchItemDetail(code.toUpperCase());
      addRowFromDetail(detail);

      setpartno(null);
      setTimeout(() => autoInputRef.current?.focus?.(), 60);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not fetch");
      setTimeout(() => autoInputRef.current?.focus?.(), 60);
    } finally {
      setAutoFetching(false);
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (fromdate === null) {
      setLoading(false);
      return toast.error("Please Enter Date");
    }
    if (!itemarray.length) {
      setLoading(false);
      return toast.error("Please add at least 1 item");
    }

    setReportText({
      title: "Generating Report",
      subtitle: "Preparing report. Please wait…",
    });

    setgenrating(true);
    try {
      const fromDateStr = fromdate;
      const toDateStr = todate.toISOString();
      const itemids = itemarray.map((opt) => opt.itemid);
      const body = { itemids };

      const res = await fetch(
        `/reports/spare/stkledgerbincard/${fromDateStr}/${toDateStr}`,
        {
          method: "POST",
          headers: {
            sptoken: userToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.blob();
      const pdfUrl = URL.createObjectURL(data);
      setPdfStream(pdfUrl);

      setLoading(false);
      setgenrating(false);
    } catch (error) {
      setLoading(false);
      setgenrating(false);
      console.log(error.message);
    }
  };

  const handleDownload = async (option, reportName) => {
    try {
      const fromDateStr = fromdate;
      const toDateStr = todate.toISOString();
      const itemids = itemarray.map((opt) => opt.itemid);
      const body = { itemids };

      const res = await fetch(
        `/reports/spare/stkledgerbincard/download/${option}/${fromDateStr}/${toDateStr}`,
        {
          method: "POST",
          headers: {
            sptoken: userToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error(`Failed to fetch. Status: ${res.status}`);

      const blob = await res.blob();
      const filename =
        parseInt(option) === 1
          ? `StockLedgerBincard_${toDateStr}.pdf`
          : `StockLedgerBincard_${toDateStr}.xlsx`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.log(error.message);
    }
    console.log(`Downloading ${option} report: ${reportName}`);
  };

  const printexcel = async (e) => {
    e.preventDefault();

    if (fromdate === null) return toast.error("Please Enter Date");
    if (!itemarray.length) return toast.error("Please add at least 1 item");

    setReportText({
      title: "Generating Report",
      subtitle: "Preparing report. Please wait…",
    });

    setgenrating(true);
    try {
      const fromDateStr = fromdate;
      const toDateStr = todate.toISOString();
      const itemids = itemarray.map((opt) => opt.itemid);
      const body = { itemids };

      const data = await fetch(
        `/reports/spare/stkledgerbincard/excel/${fromDateStr}/${toDateStr}`,
        {
          method: "POST",
          headers: {
            sptoken: userToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const blob = await data.blob();
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = "Stkledgerbincard.xlsx";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setgenrating(false);
    } catch (error) {
      setgenrating(false);
      console.log(error.message);
    }
  };

  return (
    <>
      <ReportLoadingDialog
        open={genrating}
        title={reportText.title}
        subtitle={reportText.subtitle}
      />

      <Sidebar
        Title={"STOCK LEDGER BIN CARD"}
        expandedPanel={"panel3"}
        children={
          <Box component="form" sx={{ width: "100%" }} autoComplete="off">
            <Box
              sx={{
                marginTop: "4rem",
                display: "flex",
                flexWrap: "wrap",
                columnGap: "10px",
                justifyContent: "center",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  inputFormat="DD/MM/YYYY"
                  value={fromdate}
                  onChange={(newValue) => setfromdate(newValue)}
                  renderInput={(params) => (
                    <TextField required {...params} sx={{ width: "20%" }} size="small" />
                  )}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  inputFormat="DD/MM/YYYY"
                  value={todate}
                  onChange={(newValue) => settodate(newValue)}
                  renderInput={(params) => (
                    <TextField required {...params} sx={{ width: "20%" }} size="small" />
                  )}
                />
              </LocalizationProvider>

              <Button
                type="submit"
                onClick={handleReport}
                variant="contained"
                disabled={loading === true}
                component="label"
                size="large"
                sx={{ boxShadow: "2px 2px 6px  #404142" }}
                endIcon={<Task />}
              >
                {loading ? "Fetching Data..." : " Show Report"}
              </Button>

              <Button onClick={printexcel} size="small" variant="contained" color="success">
                Excel
              </Button>
            </Box>

            {/* ✅ No modal: just show/hide add panel */}
            {/* <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                sx={{ width: "200px" }}
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowAddPanel((p) => !p);
                  setTimeout(() => {
                    if (!showAddPanel) manualInputRef.current?.focus?.();
                  }, 80);
                }}
                startIcon={<AddCircleOutline />}
              >
                {showAddPanel ? "HIDE ADD PANEL" : "ADD ITEMS"}
              </Button>
            </Box> */}

              <Box sx={{ mt: 2, p: 2, border: "1px solid #ddd", borderRadius: 1 }}>
                <ToggleButtonGroup
                  color="primary"
                  value={mode}
                  exclusive
                  onChange={(e, v) => {
                    if (!v) return;
                    setMode(v);
                    setpartno(null);
                    setManualCode("");
                    setTimeout(() => {
                      if (v === "manual") manualInputRef.current?.focus?.();
                      else autoInputRef.current?.focus?.();
                    }, 80);
                  }}
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="manual">Manual (Default)</ToggleButton>
                  <ToggleButton value="auto">Auto</ToggleButton>
                </ToggleButtonGroup>

                <Divider sx={{ mb: 2 }} />

                {mode === "manual" && (
                  <TextField
                    fullWidth
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Type Item Code and press Enter"
                    variant="outlined"
                    size="small"
                    inputRef={manualInputRef}
                    disabled={manualFetching}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!manualFetching) handleManualEnter();
                      }
                    }}
                    InputProps={{
                      endAdornment: manualFetching ? <CircularProgress size={18} /> : null,
                    }}
                  />
                )}

                {mode === "auto" && (
                  <Autocomplete
                    id="partno"
                    value={partno}
                    onChange={handleAutoChange}
                    options={allparts}
                    getOptionLabel={(option) => option.partdetails || ""}
                    filterOptions={filterOptions}
                    loading={autoFetching}
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type and press Enter to add"
                        variant="outlined"
                        size="small"
                        inputRef={autoInputRef}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {autoFetching ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              </Box>
       

            <Box sx={{ width: "100%", marginTop: "2rem" }}>
              <DataGrid
                sx={{ width: "100%", height: 300 }}
                rows={itemarray}
                columns={columns}
                getRowId={(opt) => opt.srlno}
                initialState={{
                  pagination: { paginationModel: { pageSize: 5 } },
                }}
                pageSizeOptions={[5]}
                disableRowSelectionOnClick
                density="compact"
                disableColumnMenu
              />
            </Box>

            {pdfStream && (
              <ReportNavigation onDownload={handleDownload} reportName={"StockLedgerBincard"} />
            )}

            <Box sx={{ backgroundColor: "white" }}>
              {pdfStream && (
                <iframe src={pdfStream} width="100%" height="680px" title="report" />
              )}
            </Box>
          </Box>
        }
      />

      {redirectToLoginPage()}
    </>
  );
};

export default StockLedgerBinCard;
